import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, Check, Copy, FileText, ListTodo, Mail, Phone, RefreshCw, Sparkles, Trash2, UserPlus } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtEuro } from "@/lib/constants";
import { statusOf, SOURCES, ACTION_TYPES, QUOTE_STATUS_LABELS, frDate, frDateTime, isActive } from "./constants";

const EMPTY = {
  name: "", contact_name: "", email: "", phone: "", instagram: "", sector: "", address: "",
  notes: "", next_followup: "", source: "", service: "", value_kind: "", value_amount: "", value_monthly: "",
  fonction: "", site_web: "", linkedin: "", responsible: "", tags: "",
};
const sel = "mt-1.5 w-full text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white";

export default function ProspectModal({ prospect, onClose, onSaved, onCreateRdv }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(() => (prospect ? {
    name: prospect.name || "", contact_name: prospect.contact_name || "", email: prospect.email || "",
    phone: prospect.phone || "", instagram: prospect.instagram || "", sector: prospect.sector || "",
    address: prospect.address || "", notes: prospect.notes || "", next_followup: prospect.next_followup || "",
    source: prospect.source || "", service: prospect.service || "",
    value_kind: prospect.value_kind || "", value_amount: prospect.value_amount || "", value_monthly: prospect.value_monthly || "",
    fonction: prospect.fonction || "", site_web: prospect.site_web || "", linkedin: prospect.linkedin || "",
    responsible: prospect.responsible || "", tags: (prospect.tags || []).join(", "),
  } : EMPTY));
  const [action, setAction] = useState(() => prospect?.next_action
    || { type: "", date: "", time: "", note: "" });
  const [pitch, setPitch] = useState(prospect?.last_pitch || "");
  const [channel, setChannel] = useState("email");
  const [pitchLoading, setPitchLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [dupAlert, setDupAlert] = useState(null);
  const [activity, setActivity] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [note, setNote] = useState("");
  const [quoteForm, setQuoteForm] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setA = (k) => (e) => setAction((a) => ({ ...a, [k]: e.target.value }));

  const loadActivity = () => prospect
    && api.get(`/prospects/${prospect.id}/activity`).then((r) => setActivity(r.data)).catch(() => {});
  const loadQuotes = () => prospect
    && api.get("/quotes").then((r) => setQuotes((Array.isArray(r.data) ? r.data : [])
      .filter((q) => q.prospect_id === prospect.id))).catch(() => {});
  useEffect(() => { loadActivity(); loadQuotes(); }, [prospect?.id]); // eslint-disable-line

  const save = async (force = false) => {
    if (!form.name.trim()) { toast.error("Nom du prospect requis"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        next_followup: form.next_followup || null, source: form.source || null,
        value_kind: form.value_kind || null,
        value_amount: form.value_amount || null, value_monthly: form.value_monthly || null,
        next_action: action.type && action.date ? action : null,
        tags: (form.tags || "").split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (!prospect && !force) {
        const { data: chk } = await api.post("/prospects/check-duplicate",
          { email: form.email, phone: form.phone, name: form.name });
        if (chk.duplicates.length) { setDupAlert(chk.duplicates); setSaving(false); return; }
      }
      if (prospect) {
        await api.patch(`/prospects/${prospect.id}`, payload);
        toast.success("Prospect mis à jour");
      } else {
        await api.post("/prospects", payload);
        toast.success("Prospect ajouté au pipeline");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const genPitch = async () => {
    setPitchLoading(true);
    try {
      const { data } = await api.post(`/prospects/${prospect.id}/pitch`, { channel });
      setPitch(data.pitch);
      toast.success("Pitch généré");
    } catch (e) { toast.error(apiError(e)); }
    finally { setPitchLoading(false); }
  };

  const convert = async () => {
    if (!window.confirm(`Convertir « ${prospect.name} » en client ? La fiche client reprendra ses informations, RDV et devis.`)) return;
    try {
      const { data } = await api.post(`/prospects/${prospect.id}/convert`);
      toast.success(data.existing ? "Client existant lié (aucun doublon créé)" : "Prospect converti en client");
      window.dispatchEvent(new Event("sf:clients"));
      onSaved(); onClose();
      navigate(`/clients/${data.client_id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const createQuote = async () => {
    const amount = parseFloat(quoteForm.amount) || 0;
    if (!quoteForm.title.trim()) { toast.error("Indiquez la prestation"); return; }
    try {
      await api.post("/accounting/quotes", {
        prospect_id: prospect.id,
        items: [{ prestation: quoteForm.title.trim(), description: "", qty: 1, price_ht: amount }],
        tva_mode: "non_assujetti",
      });
      toast.success("Devis créé en brouillon — visible dans Comptabilité → Devis");
      setQuoteForm(null);
      loadQuotes(); loadActivity();
    } catch (e) { toast.error(apiError(e)); }
  };

  const addTask = async () => {
    try {
      await api.post("/tasks", {
        title: `[Prospect] ${prospect.name} — suivi commercial`,
        deadline: new Date().toISOString().slice(0, 10),
        prospect_id: prospect.id, group_label: "Prospection",
      });
      toast.success("Tâche ajoutée à la To-do");
    } catch (e) { toast.error(apiError(e)); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/prospects/${prospect.id}/note`, { text: note.trim() });
    setNote("");
    loadActivity();
  };

  const remove = async () => {
    if (!window.confirm("Supprimer définitivement ce prospect ?")) return;
    await api.delete(`/prospects/${prospect.id}`);
    toast.success("Prospect supprimé");
    onSaved(); onClose();
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="prospect-modal" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 flex-wrap">
            {prospect ? prospect.name : "Nouveau prospect"}
            {prospect && <span className={`text-[10px] font-medium border rounded-full px-2 py-0.5 ${statusOf(prospect.status).chip}`} data-testid="prospect-status-chip">{statusOf(prospect.status).label}</span>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Entreprise *</Label><Input data-testid="prospect-name" className="mt-1.5" value={form.name} onChange={set("name")} /></div>
            <div><Label>Contact</Label><Input data-testid="prospect-contact" className="mt-1.5" value={form.contact_name} onChange={set("contact_name")} /></div>
            <div><Label>Fonction</Label><Input data-testid="prospect-fonction" className="mt-1.5" value={form.fonction} onChange={set("fonction")} placeholder="Ex : Gérante" /></div>
            <div><Label>Email</Label><Input data-testid="prospect-email" type="email" className="mt-1.5" value={form.email} onChange={set("email")} /></div>
            <div><Label>Téléphone</Label><Input data-testid="prospect-phone" className="mt-1.5" value={form.phone} onChange={set("phone")} /></div>
            <div><Label>Site web</Label><Input data-testid="prospect-siteweb" className="mt-1.5" value={form.site_web} onChange={set("site_web")} placeholder="https://…" /></div>
            <div><Label>LinkedIn</Label><Input data-testid="prospect-linkedin" className="mt-1.5" value={form.linkedin} onChange={set("linkedin")} placeholder="https://linkedin.com/in/…" /></div>
            <div><Label>Instagram</Label><Input data-testid="prospect-instagram" className="mt-1.5" placeholder="@compte" value={form.instagram} onChange={set("instagram")} /></div>
            <div><Label>Responsable</Label><Input data-testid="prospect-responsible" className="mt-1.5" value={form.responsible} onChange={set("responsible")} placeholder="Ex : Aurélie" /></div>
            <div><Label>Secteur</Label><Input data-testid="prospect-sector" className="mt-1.5" value={form.sector} onChange={set("sector")} /></div>
            <div><Label>Tags</Label><Input data-testid="prospect-tags" className="mt-1.5" value={form.tags} onChange={set("tags")} placeholder="chaud, instagram… (virgules)" /></div>
            <div className="col-span-2"><Label>Adresse</Label><Input data-testid="prospect-address" className="mt-1.5" value={form.address} onChange={set("address")} placeholder="Reprise sur la fiche client et les devis" /></div>
            <div>
              <Label>Source</Label>
              <select data-testid="prospect-source" className={sel} value={form.source} onChange={set("source")}>
                <option value="">—</option>
                {SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div><Label>Service recherché</Label><Input data-testid="prospect-service" className="mt-1.5" value={form.service} onChange={set("service")} placeholder="Ex : Community management" /></div>
          </div>

          <div className="border border-neutral-200 rounded-lg p-3 space-y-2.5">
            <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">Valeur commerciale</p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Type</Label>
                <select data-testid="prospect-value-kind" className={sel} value={form.value_kind} onChange={set("value_kind")}>
                  <option value="">—</option>
                  <option value="ponctuel">Ponctuel</option>
                  <option value="recurrent">Récurrent</option>
                </select>
              </div>
              {form.value_kind === "ponctuel" && (
                <div className="col-span-2"><Label>Montant potentiel (€)</Label><Input data-testid="prospect-value-amount" type="number" min="0" className="mt-1.5" value={form.value_amount} onChange={set("value_amount")} /></div>
              )}
              {form.value_kind === "recurrent" && (
                <div className="col-span-2"><Label>Montant mensuel (€)</Label><Input data-testid="prospect-value-monthly" type="number" min="0" className="mt-1.5" value={form.value_monthly} onChange={set("value_monthly")} /></div>
              )}
            </div>
          </div>

          <div className="border border-neutral-200 rounded-lg p-3 space-y-2.5">
            <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">Prochaine action</p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Type</Label>
                <select data-testid="action-type" className={sel} value={action.type} onChange={setA("type")}>
                  <option value="">—</option>
                  {ACTION_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div><Label>Date</Label><Input data-testid="action-date" type="date" className="mt-1.5" value={action.date} onChange={setA("date")} /></div>
              <div><Label>Heure</Label><Input data-testid="action-time" type="time" className="mt-1.5" value={action.time || ""} onChange={setA("time")} /></div>
            </div>
            <Input data-testid="action-note" value={action.note || ""} onChange={setA("note")} placeholder="Note (optionnel)" />
            <p className="text-[11px] text-neutral-400">Une action datée crée automatiquement une tâche dans votre To-do.</p>
          </div>

          <div>
            <Label>Prochaine relance</Label>
            <Input data-testid="prospect-followup" type="date" className="mt-1.5" value={form.next_followup} onChange={set("next_followup")} />
          </div>
          <div>
            <Label>Notes</Label>
            <textarea data-testid="prospect-notes" rows={3} value={form.notes} onChange={set("notes")}
              placeholder="Contexte, besoins exprimés, points de contact…"
              className="mt-1.5 w-full text-sm border border-neutral-200 rounded-md px-3 py-2 bg-white" />
          </div>

          {dupAlert && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2" data-testid="prospect-dup-alert">
              <p className="text-sm font-medium text-amber-800">Doublon possible — correspondance trouvée :</p>
              {dupAlert.map((d) => (
                <p key={d.id} className="text-xs text-amber-700">• {d.name} ({d.type === "client" ? "client existant" : "prospect"}, via {d.via})</p>
              ))}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setDupAlert(null)}>Annuler</Button>
                <Button size="sm" onClick={() => { setDupAlert(null); save(true); }} data-testid="prospect-dup-force"
                  className="bg-amber-600 hover:bg-amber-700 text-white">Créer quand même</Button>
              </div>
            </div>
          )}

          <Button data-testid="prospect-save" onClick={() => save(false)} disabled={saving} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white">
            {saving ? "Enregistrement…" : prospect ? "Enregistrer les modifications" : "Ajouter au pipeline"}
          </Button>

          {prospect && (
            <div className="pt-3 border-t border-neutral-100 space-y-3">
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">Actions rapides</p>
              <div className="grid grid-cols-3 gap-2" data-testid="prospect-quick-actions">
                <a href={prospect.phone ? `tel:${prospect.phone}` : undefined} data-testid="prospect-quick-call"
                  className={`flex items-center justify-center gap-1.5 text-xs border rounded-lg px-2 py-2 transition-colors ${prospect.phone ? "border-neutral-200 hover:bg-neutral-50" : "border-neutral-100 text-neutral-300 pointer-events-none"}`}>
                  <Phone className="w-3.5 h-3.5" /> Appeler
                </a>
                <a href={prospect.email ? `mailto:${prospect.email}` : undefined} data-testid="prospect-quick-email"
                  className={`flex items-center justify-center gap-1.5 text-xs border rounded-lg px-2 py-2 transition-colors ${prospect.email ? "border-neutral-200 hover:bg-neutral-50" : "border-neutral-100 text-neutral-300 pointer-events-none"}`}>
                  <Mail className="w-3.5 h-3.5" /> Email
                </a>
                <button onClick={() => onCreateRdv(prospect)} data-testid="prospect-quick-rdv"
                  className="flex items-center justify-center gap-1.5 text-xs border border-green-300 text-green-700 rounded-lg px-2 py-2 hover:bg-green-50 transition-colors">
                  <CalendarCheck className="w-3.5 h-3.5" /> Fixer un RDV
                </button>
                <button onClick={() => setQuoteForm({ title: form.service || "", amount: form.value_amount || form.value_monthly || "" })}
                  data-testid="prospect-quick-quote"
                  className="flex items-center justify-center gap-1.5 text-xs border border-amber-300 text-amber-700 rounded-lg px-2 py-2 hover:bg-amber-50 transition-colors">
                  <FileText className="w-3.5 h-3.5" /> Créer un devis
                </button>
                <button onClick={addTask} data-testid="prospect-quick-task"
                  className="flex items-center justify-center gap-1.5 text-xs border border-neutral-200 rounded-lg px-2 py-2 hover:bg-neutral-50 transition-colors">
                  <ListTodo className="w-3.5 h-3.5" /> Ajouter une tâche
                </button>
                {!prospect.converted_client_id && (
                  <button onClick={convert} data-testid="prospect-quick-convert"
                    className="flex items-center justify-center gap-1.5 text-xs border border-emerald-300 text-emerald-700 rounded-lg px-2 py-2 hover:bg-emerald-50 transition-colors">
                    <UserPlus className="w-3.5 h-3.5" /> Créer un client
                  </button>
                )}
              </div>
              {prospect.status === "rdv_fixe" && prospect.appointment_id && (
                <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2" data-testid="prospect-rdv-info">
                  RDV fixé — visible dans la page RDV et dans l'agenda.
                </p>
              )}
              {prospect.converted_client_id && (
                <p className="text-xs text-green-700 font-medium" data-testid="prospect-converted">Client créé ✓ — historique de prospection conservé ci-dessous.</p>
              )}

              {quoteForm && (
                <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-3 space-y-2" data-testid="prospect-quote-form">
                  <p className="text-xs font-semibold text-amber-800">Nouveau devis pour {prospect.name}</p>
                  <Input value={quoteForm.title} onChange={(e) => setQuoteForm((q) => ({ ...q, title: e.target.value }))}
                    placeholder="Prestation (ex : Community management)" data-testid="prospect-quote-title" />
                  <Input type="number" min="0" value={quoteForm.amount} onChange={(e) => setQuoteForm((q) => ({ ...q, amount: e.target.value }))}
                    placeholder="Montant HT (€)" data-testid="prospect-quote-amount" />
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setQuoteForm(null)} className="flex-1">Annuler</Button>
                    <Button size="sm" onClick={createQuote} data-testid="prospect-quote-save"
                      className="flex-1 bg-amber-600 hover:bg-amber-700 text-white">Créer le devis</Button>
                  </div>
                </div>
              )}

              {quotes.length > 0 && (
                <div className="space-y-1.5" data-testid="prospect-quotes">
                  <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">Devis liés</p>
                  {quotes.map((q) => (
                    <div key={q.id} className="flex items-center justify-between text-sm border border-neutral-100 rounded-lg px-3 py-2"
                      data-testid={`prospect-quote-${q.id}`}>
                      <span><strong>{q.number || "Brouillon"}</strong> · {fmtEuro(q.total_ttc)}</span>
                      <span className="flex items-center gap-2">
                        <span className="text-xs text-neutral-500">{QUOTE_STATUS_LABELS[q.status] || q.status}</span>
                        {q.status === "accepte" && !prospect.converted_client_id && (
                          <button onClick={convert} data-testid="prospect-quote-convert"
                            className="text-xs font-medium text-emerald-700 hover:underline">Convertir en client</button>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">Historique</p>
                <div className="flex gap-2">
                  <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ajouter une note à la timeline…"
                    data-testid="prospect-note-input" onKeyDown={(e) => e.key === "Enter" && addNote()} />
                  <Button variant="outline" onClick={addNote} data-testid="prospect-note-add">+</Button>
                </div>
                <div className="space-y-1 max-h-44 overflow-y-auto" data-testid="prospect-timeline">
                  {activity.length === 0 && <p className="text-xs text-neutral-300">Aucun événement pour le moment.</p>}
                  {activity.map((a) => (
                    <p key={a.id} className="text-xs text-neutral-600" data-testid={`activity-${a.id}`}>
                      <span className="text-neutral-400">{frDateTime(a.at)}</span> — {a.label}
                    </p>
                  ))}
                </div>
              </div>

              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">Pitch de prospection IA</p>
              <div className="flex gap-2">
                <select data-testid="pitch-channel" value={channel} onChange={(e) => setChannel(e.target.value)}
                  className="text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white">
                  <option value="email">Email</option>
                  <option value="instagram">Instagram DM</option>
                </select>
                <Button variant="outline" data-testid="pitch-generate" onClick={genPitch} disabled={pitchLoading} className="gap-1.5 flex-1">
                  {pitchLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {pitchLoading ? "Rédaction…" : "Générer un pitch personnalisé"}
                </Button>
              </div>
              {pitch && (
                <div className="relative">
                  <p className="text-sm bg-neutral-50 border border-neutral-200 rounded-lg p-3.5 whitespace-pre-line" data-testid="pitch-text">{pitch}</p>
                  <button data-testid="pitch-copy" onClick={async () => { await navigator.clipboard.writeText(pitch); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    className="absolute top-2 right-2 text-neutral-400 hover:text-neutral-900 transition-colors" title="Copier">
                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="outline" data-testid="prospect-delete" onClick={remove}
                  className="gap-1.5 text-red-600 hover:bg-red-50 ml-auto">
                  <Trash2 className="w-4 h-4" /> Supprimer
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
